/*
 * This is the Provider Profile Page that has
 * the patient sees, when they click on any
 * provider card. Holds all relevant provider details
 * as well as availability, and reviews
 */

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getProvider } from "../APIs/providerApi.js";
import {
  addFavorite,
  removeFavorite,
  getFavoriteProviders,
} from "../APIs/favoriteApi.js";
import {
  createOrUpdateRating,
  getProviderRatings,
} from "../APIs/ratingsApi.js";
import { getPatient } from "../APIs/patientService.js";
import {
  getFreeStartTimes,
  getFreeStartTimesBooked,
} from "../APIs/availability.js";
import { fetchCurrentUser } from "../APIs/authApi";
import keycloak from "../APIs/keycloak";
import { useBackendReady } from "../APIs/BackendReadyContext";
import { showToast } from "../APIs/utils/toastManager";
import { UserRound } from "lucide-react";
import Header from "../modals/Header.js";
import BookingModal from "../Patient/BookingModal.js";
import WaitlistModal from "./components/WaitlistModal.js";

// ── Calendar helpers ──────────────────────────────────────────────────────────
function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: daysInPrev - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, current: true });
  while (cells.length < 42)
    cells.push({
      day: cells.length - firstDay - daysInMonth + 1,
      current: false,
    });
  return cells;
}

function groupSlots(zdtList, zone) {
  const morning = [],
    afternoon = [],
    evening = [];
  for (const zdt of zdtList) {
    const iso = String(zdt).split("[")[0];
    const h = parseInt(
      new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hour: "numeric",
        hour12: false,
      }).format(new Date(iso)),
      10,
    );
    if (h < 12) morning.push(zdt);
    else if (h < 17) afternoon.push(zdt);
    else evening.push(zdt);
  }
  return { morning, afternoon, evening };
}
// ─────────────────────────────────────────────────────────────────────────────

function ProviderProfilePage() {
  const backendReady = useBackendReady();
  //const { id: providerId, patientId } = useParams();
  const { id: providerId, patientId: patientIdParam } = useParams();
  const location = useLocation();
  const qsPatientId = new URLSearchParams(location.search).get("patientId");

  // final patientId source of truth
  const patientId =
    patientIdParam ||
    qsPatientId ||
    sessionStorage.getItem("patientId") ||
    localStorage.getItem("patientId");

  //console.log("PatientId ", patientId);
  //console.log("Patient Param ", patientIdParam);

  // (Optional) guard so you don’t navigate with “undefined”
  if (!patientId) {
    //console.warn("No patientId found in params/query/storage");
    // You can show a gentle message or redirect to login/signup
  }
  //const patientId = 1; // Replace with logged-in patient's ID dynamically
  const totalStars = 5;

  const [provider, setProvider] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [reviewsWithNames, setReviewsWithNames] = useState([]);
  // --- ADDED
  const [bookedStarts, setBookedStarts] = useState([]);

  // Auth / gating state
  const [currentUser, setCurrentUser] = useState(null);
  const [, setUserLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Inline booking modal state
  const [bookingSlot, setBookingSlot] = useState(null);

  // Waitlist modal state
  const [waitlistSlot, setWaitlistSlot] = useState(null);

  // Calendar view state
  const [calViewYear, setCalViewYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [calViewMonth, setCalViewMonth] = useState(() => new Date().getMonth());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const availInitialized = useRef(false);

  // --- ADDED: Availability state
  const [availDate, setAvailDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [availZone, setAvailZone] = useState("America/New_York");
  const [telehealth, setTelehealth] = useState(""); // "", "true", "false"
  const [freeStarts, setFreeStarts] = useState([]); // array of ISO strings
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [errorAvail, setErrorAvail] = useState("");

  //Gating for sign In/Sign Up
  const openAuthModal = () => setShowAuthModal(true);
  const closeAuthModal = () => setShowAuthModal(false);

  const handleSignIn = () => {
    // ✅ Add this check first
    if (!backendReady) {
      showToast("MedMaze is loading, please wait.", "info");
      return;
    }

    // redirect back here after login
    keycloak?.login({ redirectUri: window.location.href });
  };

  const handleSignUp = () => {
    keycloak?.register({ redirectUri: window.location.href });
  };

  // --- ADDED: formatter respecting chosen zone
  function fmtInZone(isoLike, zone) {
    try {
      const dt = new Date(String(isoLike).replace("Z[", "Z[")); // be tolerant
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: undefined,
        timeStyle: "short",
        timeZone: zone || "America/New_York",
      }).format(dt);
    } catch {
      return isoLike ?? "";
    }
  }

  //Gating for sign up
  useEffect(() => {
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me); // null if unauthenticated
      } catch (e) {
        console.error(
          "Failed to fetch current user on ProviderProfilePage:",
          e,
        );
        setCurrentUser(null);
      } finally {
        setUserLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchReviewsWithNames = async () => {
      const updatedReviews = await Promise.all(
        reviews.map(async (review) => {
          try {
            const patient = await getPatient(review.patientId);
            return {
              ...review,
              patientName: `${patient.firstName} ${patient.lastName}`,
            };
          } catch (error) {
            console.error("Error fetching patient:", error);
            return { ...review, patientName: "Anonymous" };
          }
        }),
      );
      setReviewsWithNames(updatedReviews);
    };

    fetchReviewsWithNames();
  }, [reviews]);

  // Fetch provider data
  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const data = await getProvider(providerId);
        setProvider(data);
      } catch (error) {
        console.error("Failed to fetch provider:", error);
      }
    };
    fetchProvider();
  }, [providerId]);

  // Init availDate from provider's earliest availability (once, on first load)
  useEffect(() => {
    if (availInitialized.current || !provider?.earliestAvailability) return;
    const iso = String(provider.earliestAvailability).split("[")[0];
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return; // past — keep today as default
    availInitialized.current = true;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: availZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const year = parts.find((p) => p.type === "year").value;
    const month = parts.find((p) => p.type === "month").value;
    const day = parts.find((p) => p.type === "day").value;
    setAvailDate(`${year}-${month}-${day}`);
  }, [provider]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync calendar view month/year and reset selected slot when date changes
  useEffect(() => {
    const [y, m] = availDate.split("-").map(Number);
    setCalViewYear(y);
    setCalViewMonth(m - 1);
    setSelectedSlot(null);
  }, [availDate]);

  // Fetch ratings data
  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const data = await getProviderRatings(providerId);
        setAverageRating(data.averageRating || 0);
        setReviewCount(data.reviewCount || 0);
        setReviews(data.reviews || []);
      } catch (error) {
        console.error("Error fetching ratings:", error);
      }
    };
    fetchRatings();
  }, [providerId]);

  // --- ADDED: Fetch FREE starts (blue) and BOOKED starts (red)
  // --- REFACTORED: use API helpers like in your ViewAvailabilities code
  useEffect(() => {
    let cancelled = false;

    async function fetchFree() {
      try {
        const data = await getFreeStartTimes(Number(providerId), {
          date: availDate,
          zone: availZone,
          telehealth: telehealth === "" ? undefined : telehealth,
        });
        if (!cancelled) setFreeStarts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setErrorAvail(e?.message || "Failed to load start times");
          setFreeStarts([]);
        }
      }
    }

    async function fetchBooked() {
      try {
        const data = await getFreeStartTimesBooked(Number(providerId), {
          date: availDate,
          zone: availZone,
          telehealth: telehealth === "" ? undefined : telehealth,
        });
        if (!cancelled) setBookedStarts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setErrorAvail(e?.message || "Failed to load scheduled appointments");
          setBookedStarts([]);
        }
      }
    }

    async function load() {
      if (!providerId) return;
      setLoadingAvail(true);
      setErrorAvail("");
      try {
        await Promise.all([fetchFree(), fetchBooked()]);
      } finally {
        if (!cancelled) setLoadingAvail(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [providerId, availDate, availZone, telehealth]);

  // Check if provider is already favorited
  useEffect(() => {
    const checkIfFavorited = async () => {
      if (!patientId || !providerId) return;

      try {
        const favoriteProviders = await getFavoriteProviders(patientId);
        // Check if current provider is in the favorites list
        const isFav = favoriteProviders.some(
          (favProvider) => favProvider.providerId === Number(providerId),
        );
        setIsFavorited(isFav);
      } catch (error) {
        console.error("Error checking favorite status:", error);
      }
    };

    checkIfFavorited();
  }, [patientId, providerId]);

  // Submit review
  const submitReview = async () => {
    if (!newRating) {
      alert("Please select a star rating.");
      return;
    }

    try {
      const ratingDTO = {
        providerId,
        patientId,
        rating: newRating,
        reviewText: newComment,
      };

      await createOrUpdateRating(ratingDTO);

      // Refresh after submit
      const data = await getProviderRatings(providerId);
      setAverageRating(data.averageRating || 0);
      setReviewCount(data.reviewCount || 0);
      setReviews(data.reviews || []);

      setNewRating(0);
      setNewComment("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Failed to submit review");
    }
  };

  // Toggle favorites (placeholder, replace after auth)
  //const toggleFavorite = () => setIsFavorited((prev) => !prev);
  //Implement toggleFavorite after login/authentication is implemented
  //By removing comment above and adding this
  /*const toggleFavorite = async () => {
    try {
      if (isFavorited) {
        await removeFavorite(patientId, providerId);
        setIsFavorited(false);
      } else {
        await addFavorite(patientId, providerId);
        setIsFavorited(true);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };*/

  const toggleFavorite = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    try {
      if (isFavorited) {
        await removeFavorite(providerId);
        setIsFavorited(false);
      } else {
        await addFavorite(providerId);
        setIsFavorited(true);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  // Render stars helper
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= totalStars; i++) {
      if (rating >= i) {
        stars.push(<span key={i}>★</span>);
      } else if (rating >= i - 0.5) {
        stars.push(<span key={i}>☆</span>);
      } else {
        stars.push(
          <span key={i} className="text-gray-300">
            ★
          </span>,
        );
      }
    }
    return stars;
  };

  if (!provider)
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">Loading provider...</p>
        </div>
      </>
    );

  return (
    <>
      <Header />

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 relative">
            <button
              type="button"
              onClick={closeAuthModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4 text-center">
              Welcome to MedMaze
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Sign in or Sign Up to book appointments, join waitlists, add
              favorites, matches and alerts
            </p>
            <button
              type="button"
              onClick={handleSignIn}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg mb-3 transition-colors"
            >
              Sign In
            </button>
            <div className="flex items-center my-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="mx-2 text-gray-400 text-xs">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={handleSignUp}
              className="w-full border border-teal-600 text-teal-600 hover:bg-teal-50 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50">
        {/* Sub-nav */}
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <Link
            to="/ProviderSearchPage"
            className="inline-flex items-center text-sm text-teal-600 hover:underline font-medium"
          >
            ← Back to search
          </Link>
        </div>

        {/* Main two-column layout */}
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* LEFT PANEL — at-a-glance */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-sm p-6 sticky top-6">
              {/* Avatar */}
              <div className="flex justify-center">
                {provider.profilePictureUrl ? (
                  <img
                    src={provider.profilePictureUrl}
                    alt={`Dr. ${provider.firstName} ${provider.lastName}`}
                    className="h-20 w-20 rounded-full object-cover border-2 border-teal-200"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                    <UserRound size={44} className="text-gray-400" />
                  </div>
                )}
              </div>

              {/* Name */}
              <h1 className="mt-4 text-2xl font-bold text-gray-900 text-center">
                Dr. {provider.firstName} {provider.lastName}
              </h1>

              {/* Specialty badges */}
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {provider.specialties?.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 px-3 py-1 text-xs font-semibold text-teal-700"
                  >
                    {s.name}
                  </span>
                ))}
              </div>

              {/* Accepting new patients */}
              {provider.acceptingNewPatients === true && (
                <div className="mt-2 flex justify-center">
                  <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold">
                    Accepting New Patients
                  </span>
                </div>
              )}

              {/* Stars */}
              <div className="mt-4 flex justify-center text-2xl text-yellow-500">
                {reviewCount > 0
                  ? renderStars(averageRating)
                  : [...Array(totalStars)].map((_, i) => (
                      <span key={i} className="text-gray-300">
                        ★
                      </span>
                    ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-1">
                {reviewCount > 0
                  ? `${averageRating.toFixed(1)} / ${totalStars} · ${reviewCount} review${reviewCount > 1 ? "s" : ""}`
                  : "No ratings yet"}
              </p>

              {/* Action buttons */}
              <div className="mt-5 flex flex-col gap-2">
                <Link
                  to="/EmailProvider"
                  state={{
                    providerName: `${provider.firstName} ${provider.lastName}`,
                  }}
                  className="w-full text-center bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
                >
                  ✉ Email
                </Link>
                <button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-colors">
                  📞 Call
                </button>
                <button
                  onClick={toggleFavorite}
                  className={`w-full font-semibold py-2 px-4 rounded-lg text-sm transition-colors ${
                    isFavorited
                      ? "bg-teal-600 text-white hover:bg-teal-700"
                      : "border border-teal-600 text-teal-600 hover:bg-teal-50"
                  }`}
                >
                  {isFavorited ? "★ Saved" : "☆ Add to Favorites"}
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — detail sections */}
          <div className="lg:col-span-2 space-y-5">
            {/* Bio */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2">
                About
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm">
                {provider.bio || "No bio available."}
              </p>
            </div>

            {/* Key details */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-3">
                Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                <div>
                  <span className="font-semibold text-gray-900">
                    Location(s):{" "}
                  </span>
                  {provider.facilityInformation
                    ?.map((f) => `${f.city}, ${f.state}`)
                    .join(", ") || "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">
                    Languages:{" "}
                  </span>
                  {provider.languages?.map((l) => l.languageName).join(", ") ||
                    "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">
                    Education:{" "}
                  </span>
                  {provider.educationRecords
                    ?.map((e) => `${e.degree} - ${e.institutionName}`)
                    .join(", ") || "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">
                    Board Certification:{" "}
                  </span>
                  {provider.boardCertifications
                    ?.map((b) => b.name)
                    .join(", ") || "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Gender: </span>
                  {provider.gender || "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Phone: </span>
                  {provider.phoneNumber || "N/A"}
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Fax: </span>
                  {provider.faxNumber || "N/A"}
                </div>
              </div>
            </div>

            {/* Facility Information */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-3">
                Facility Information
              </h2>
              <div className="space-y-2">
                {provider.facilityInformation
                  ?.sort((a, b) =>
                    a.facilityName?.localeCompare(b.facilityName || "", "en", {
                      sensitivity: "base",
                    }),
                  )
                  .map((f, idx) => (
                    <details
                      key={idx}
                      className="border border-gray-200 rounded-xl bg-gray-50 p-3"
                    >
                      <summary
                        className="cursor-pointer text-gray-900 font-medium flex items-center gap-2 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="mr-1 text-teal-600">▸</span>
                        {f.facilityName || "Unnamed Facility"}
                        {f.addressType && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              f.addressType === "PRIMARY"
                                ? "bg-teal-100 text-teal-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {f.addressType}
                          </span>
                        )}
                      </summary>
                      <div className="ml-5 mt-2 text-sm text-gray-700 space-y-1">
                        <p>
                          {[f.addressLine, f.city, f.state, f.country]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        {f.phoneNumber && <p>📞 {f.phoneNumber}</p>}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([f.addressLine, f.city, f.state, f.country].filter(Boolean).join(", "))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-teal-600 hover:underline"
                        >
                          🔹 Directions
                        </a>
                        {f.facilityEmail && (
                          <p>
                            ✉️{" "}
                            <a
                              href={`mailto:${f.facilityEmail}`}
                              className="text-teal-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {f.facilityEmail}
                            </a>
                          </p>
                        )}
                        {f.facilityWebsite && (
                          <p>
                            🌐{" "}
                            <a
                              href={`https://${f.facilityWebsite.replace(/^https?:\/\//, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {f.facilityWebsite}
                            </a>
                          </p>
                        )}
                      </div>
                    </details>
                  )) || <p className="text-gray-500 text-sm">N/A</p>}
              </div>
            </div>

            {/* Insurance */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-3">
                Insurance Accepted
              </h2>
              <div className="flex flex-wrap gap-2">
                {provider.insurances?.map((i) => (
                  <span
                    key={i.insuranceId}
                    className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700"
                  >
                    {i.planName}
                  </span>
                )) || <span className="text-gray-500 text-sm">N/A</span>}
              </div>
            </div>

            {/* Availability */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-4">
                Availability
              </h2>

              {/* Filters row */}
              <div className="flex flex-wrap gap-3 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Time Zone
                  </label>
                  <select
                    value={availZone}
                    onChange={(e) => setAvailZone(e.target.value)}
                    className="h-8 border border-gray-300 rounded-lg px-2 text-xs focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none"
                  >
                    {[
                      "America/New_York",
                      "America/Chicago",
                      "America/Denver",
                      "America/Los_Angeles",
                      "UTC",
                      "Europe/London",
                      "Europe/Paris",
                    ].map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Telehealth
                  </label>
                  <select
                    value={telehealth}
                    onChange={(e) => setTelehealth(e.target.value)}
                    className="h-8 border border-gray-300 rounded-lg px-2 text-xs focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none"
                  >
                    <option value="">Any</option>
                    <option value="true">Telehealth Only</option>
                    <option value="false">In-Person Only</option>
                  </select>
                </div>
              </div>

              {/* Calendar + Time slots */}
              {(() => {
                const todayDateStr = (() => {
                  const parts = new Intl.DateTimeFormat("en-US", {
                    timeZone: availZone,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  }).formatToParts(new Date());
                  return `${parts.find((p) => p.type === "year").value}-${parts.find((p) => p.type === "month").value}-${parts.find((p) => p.type === "day").value}`;
                })();

                const prevMonth = () => {
                  if (calViewMonth === 0) {
                    setCalViewMonth(11);
                    setCalViewYear((y) => y - 1);
                  } else setCalViewMonth((m) => m - 1);
                };
                const nextMonth = () => {
                  if (calViewMonth === 11) {
                    setCalViewMonth(0);
                    setCalViewYear((y) => y + 1);
                  } else setCalViewMonth((m) => m + 1);
                };

                const { morning, afternoon, evening } = groupSlots(
                  freeStarts,
                  availZone,
                );

                const SlotGroup = ({ label, slots }) =>
                  slots.length === 0 ? null : (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        {label}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {slots.map((zdt, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setSelectedSlot(zdt)}
                            className={`py-2 rounded-xl text-xs font-semibold border transition-colors ${
                              selectedSlot === zdt
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-teal-50 hover:border-teal-300"
                            }`}
                          >
                            {fmtInZone(zdt, availZone)}
                          </button>
                        ))}
                      </div>
                    </div>
                  );

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left: Custom calendar */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={prevMonth}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 text-lg font-bold"
                        >
                          ‹
                        </button>
                        <span className="text-sm font-semibold text-gray-900">
                          {new Date(calViewYear, calViewMonth).toLocaleString(
                            "en-US",
                            { month: "long", year: "numeric" },
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={nextMonth}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 text-lg font-bold"
                        >
                          ›
                        </button>
                      </div>

                      <div className="grid grid-cols-7 mb-1">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                          <div
                            key={d}
                            className="text-center text-xs font-semibold text-gray-400 py-1"
                          >
                            {d}
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-7">
                        {buildCalendarDays(calViewYear, calViewMonth).map(
                          (cell, idx) => {
                            const cellDateStr = cell.current
                              ? `${calViewYear}-${String(calViewMonth + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`
                              : null;
                            const isSelected = cellDateStr === availDate;
                            const isPast =
                              cellDateStr && cellDateStr < todayDateStr;
                            const isDisabled = !cell.current || isPast;
                            return (
                              <div
                                key={idx}
                                className="flex items-center justify-center py-0.5"
                              >
                                <button
                                  type="button"
                                  disabled={isDisabled}
                                  onClick={() =>
                                    !isDisabled && setAvailDate(cellDateStr)
                                  }
                                  className={`h-8 w-8 flex items-center justify-center rounded-full text-xs font-medium transition-colors ${
                                    !cell.current || isPast
                                      ? "text-gray-300 cursor-default"
                                      : isSelected
                                        ? "bg-teal-600 text-white"
                                        : "text-gray-900 hover:bg-teal-50 cursor-pointer"
                                  }`}
                                >
                                  {cell.day}
                                </button>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>

                    {/* Right: Time slots */}
                    <div className="flex flex-col">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        {new Date(availDate + "T00:00:00").toLocaleDateString(
                          "en-US",
                          { weekday: "long", month: "long", day: "numeric" },
                        )}
                      </p>
                      {loadingAvail ? (
                        <p className="text-gray-500 text-sm">
                          Loading times...
                        </p>
                      ) : errorAvail ? (
                        <p className="text-red-600 text-sm">{errorAvail}</p>
                      ) : freeStarts.length === 0 ? (
                        <p className="text-gray-500 text-sm">
                          No available times for this date.
                        </p>
                      ) : (
                        <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
                          <SlotGroup label="Morning" slots={morning} />
                          <SlotGroup label="Afternoon" slots={afternoon} />
                          <SlotGroup label="Evening" slots={evening} />
                        </div>
                      )}

                      {selectedSlot && (
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              if (!currentUser) {
                                openAuthModal();
                                return;
                              }
                              setBookingSlot({
                                zdt: selectedSlot,
                                providerName: `${provider.firstName} ${provider.lastName}`,
                                specialty:
                                  provider.specialties
                                    ?.map((s) => s.name)
                                    .join(", ") || "",
                                facility:
                                  provider.facilityInformation
                                    ?.map((f) => f.facilityName)
                                    .join(", ") || "",
                              });
                            }}
                            className="w-full py-2.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors"
                          >
                            Continue →
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Scheduled Appointments */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Scheduled Appointments
                </p>
                <div className="flex flex-wrap gap-2">
                  {bookedStarts.length === 0 ? (
                    <span className="text-gray-500 text-sm">
                      No scheduled appointments.
                    </span>
                  ) : (
                    bookedStarts.map((slot, idx) => (
                      <button
                        key={`booked-${slot.start}-${idx}`}
                        className="px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
                        title={slot.start}
                        onClick={() => {
                          if (!currentUser) {
                            openAuthModal();
                            return;
                          }
                          setWaitlistSlot({
                            slotStart: slot.start,
                            appointmentId: slot.appointmentId,
                          });
                        }}
                      >
                        {fmtInZone(slot.start, availZone)}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Patient Reviews */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-3">
                Patient Reviews
              </h2>
              <ul className="space-y-3 mb-4">
                {reviewsWithNames.map((review, index) => (
                  <li
                    key={index}
                    className="rounded-xl bg-teal-50 border border-teal-100 p-4"
                  >
                    <p className="text-sm font-semibold text-gray-800">
                      {review.patientName || "Anonymous"}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {review.reviewText || ""}
                    </p>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors"
              >
                Write a Review
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inline Booking Modal */}
      <BookingModal
        open={!!bookingSlot}
        onClose={() => setBookingSlot(null)}
        providerId={providerId}
        providerName={bookingSlot?.providerName || ""}
        slotZdt={bookingSlot?.zdt || ""}
        specialty={bookingSlot?.specialty || ""}
        facility={bookingSlot?.facility || ""}
      />

      {/* Waitlist Modal */}
      <WaitlistModal
        open={!!waitlistSlot}
        onClose={() => setWaitlistSlot(null)}
        providerId={providerId}
        patientId={currentUser?.patientId}
        providerName={
          provider ? `${provider.firstName} ${provider.lastName}` : ""
        }
        slotStart={waitlistSlot?.slotStart}
        appointmentId={waitlistSlot?.appointmentId}
        zone={availZone}
      />

      {/* Review Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-96">
            <h3 className="text-lg font-semibold mb-4">Write a Review</h3>
            <div className="flex space-x-1 text-3xl text-yellow-500 mb-4">
              {[...Array(totalStars)].map((_, i) => (
                <button key={i} onClick={() => setNewRating(i + 1)}>
                  {i < newRating ? "★" : "☆"}
                </button>
              ))}
            </div>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write your review (optional)"
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 text-sm"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={newRating === 0}
                className={`px-4 py-2 rounded-lg text-sm text-white ${newRating === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700"}`}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProviderProfilePage;
